import './BrandMark.css';

export interface BrandMarkProps {
  /**
   * A plane qualifier rendered as a chip after the mark — "Ops" for the operator
   * console. Deliberately a separate chip rather than part of the wordmark: the
   * mark is the product's identity and must read identically everywhere, while the
   * qualifier says *which surface you are on*, which is a different claim.
   */
  suffix?: string;
  /**
   * `md` (default) is the shell's brand slot. `lg` matches the marketing header's
   * 23px, for any product surface that needs the full-size lockup.
   */
  size?: 'md' | 'lg';
}

/**
 * The Dedupr mark — the lowercase wordmark plus its accent dot.
 *
 * **One mark, every surface.** `apps/landing` shipped this lockup and the product
 * apps rendered plain text ("Dedupr", "Dedupr Ops") with no mark at all, so the
 * marketing site and the product it sells did not look like the same company. The
 * favicon bytes were already shared via `packages/tokens/brand`; this closes the
 * same gap for the visible mark.
 *
 * **Owner decision, recorded because it diverges from the prototype.** The
 * prototype's sidebar renders `Dedupr` — capitalised, 16px, no dot. The landing
 * lockup is lowercase `dedupr` with the dot, and the owner's call (2026-07-28) is
 * that the landing mark is *the* mark. This is the second deliberate divergence
 * from the prototype after ADR-058's accent, and it is the same kind: the brand as
 * actually shipped wins over the prototype's rendering of it.
 *
 * **Built from product tokens, not landing's values.** Landing hardcodes its
 * `--land-*` marketing palette by design (it is deliberately dependency-free). This
 * reproduces the *lockup* — proportions, weight, tracking, the dot — while drawing
 * colour and type from `@dedupr/tokens`, so the mark themes with the rest of the
 * product across all six palettes and three surface modes.
 *
 * The dot uses `--accent-fg`, not `--accent-text`: it is a graphical element, so the
 * 3:1 licence is the correct one (docs-v3.10's text licence applies to `color:` on
 * text). It is also `aria-hidden` — the accessible name comes from the wordmark.
 */
export function BrandMark({ suffix, size = 'md' }: BrandMarkProps) {
  return (
    <span className={`dpr-brand dpr-brand--${size}`}>
      {/* Lowercase in the markup, not via `text-transform`: the wordmark IS
          lowercase, and a CSS transform would leave the accessible name and any
          copied text capitalised — i.e. the mark would be right for sighted users
          and wrong for everyone else. */}
      <span className="dpr-brand__word">dedupr</span>
      <span className="dpr-brand__dot" aria-hidden="true" />
      {suffix ? <span className="dpr-brand__suffix">{suffix}</span> : null}
    </span>
  );
}
